import { useCallback, useEffect, useState } from 'react';
import { LatLng } from './geoHelpers';

const STORAGE_KEY = 'userLocation';
const CHANGE_EVENT = 'user-location-change';

export type UserLocationStatus =
  | 'idle'
  | 'unsupported'
  | 'requesting'
  | 'granted'
  | 'denied';

interface LocationChange {
  location: LatLng | null;
  status: UserLocationStatus;
}

interface UserLocationInfo {
  location: LatLng | null;
  status: UserLocationStatus;
  requestLocation: () => void;
}

const readStoredLocation = (): LatLng | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!Number.isFinite(parsed?.lat) || !Number.isFinite(parsed?.lng)) {
      return null;
    }

    return { lat: parsed.lat, lng: parsed.lng };
  } catch {
    return null;
  }
};

export const useUserLocation = (): UserLocationInfo => {
  const [location, setLocation] = useState<LatLng | null>(readStoredLocation);
  const [status, setStatus] = useState<UserLocationStatus>(() =>
    readStoredLocation() ? 'granted' : 'idle'
  );

  useEffect(() => {
    if (!navigator.geolocation) {
      setStatus('unsupported');
    }
  }, []);

  useEffect(() => {
    const handleChange = (event: Event) => {
      const { detail } = event as CustomEvent<LocationChange>;
      setLocation(detail.location);
      setStatus(detail.status);
    };

    window.addEventListener(CHANGE_EVENT, handleChange);

    return () => window.removeEventListener(CHANGE_EVENT, handleChange);
  }, []);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setStatus('unsupported');
      return;
    }

    setStatus('requesting');

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const next = { lat: coords.latitude, lng: coords.longitude };
        setLocation(next);
        setStatus('granted');

        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        } catch {}

        window.dispatchEvent(
          new CustomEvent<LocationChange>(CHANGE_EVENT, {
            detail: { location: next, status: 'granted' },
          })
        );
      },
      () => {
        setLocation(null);
        setStatus('denied');

        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch {}

        window.dispatchEvent(
          new CustomEvent<LocationChange>(CHANGE_EVENT, {
            detail: { location: null, status: 'denied' },
          })
        );
      },
      { maximumAge: 5 * 60 * 1000, timeout: 10000 }
    );
  }, []);

  return { location, status, requestLocation };
};
